import { colors } from './tokens';

/**
 * Accent de couleur par FEATURE — source unique de vérité.
 *
 * UNE FEATURE = UNE COULEUR, PARTOUT. Le CV est électrique sur l'accueil, dans Carrière et dans le
 * schéma d'avant connexion ; la lettre est corail des trois côtés. C'est ce qui permet de
 * reconnaître une fonctionnalité d'un écran à l'autre sans lire le libellé.
 *
 * Ce fichier existe parce que la règle n'a pas tenu : les trois écrans portaient chacun leur copie
 * de la table, et le schéma d'avant connexion avait fini par suivre les couleurs de la MAQUETTE
 * plutôt que celles de l'application — CV violet ici, électrique ailleurs. Trois copies identiques
 * ne sont pas une convention, ce sont trois occasions de diverger.
 *
 * TEINTES ÉCHANTILLONNÉES SUR LA MAQUETTE CARRIÈRE, et versées aux tokens (voir `tokens.ts`) plutôt
 * qu'écrites en dur ici. La règle précédente — « les couleurs viennent de la charte, jamais d'une
 * maquette » — tenait tant que l'accent était un liseré ou un glyphe. Depuis que la carte est un
 * BLOC PLEIN de la couleur de la feature, la teinte occupe la moitié de l'écran : les teintes de la
 * charte 2026, calibrées pour des traits fins, y rendaient fades. Ce n'est pas une exception pour un
 * écran, c'est la palette qui a suivi l'usage.
 *
 * `cv`, `linkedin` et `offers` sont relevés au pixel sur la maquette. `letter`, `interview` et
 * `coach` n'y figurent pas : ils reprennent des arrêts du dégradé de marque, lui aussi échantillonné,
 * pour rester de la même famille. À confirmer si une maquette les montre un jour.
 *
 * `color` est l'accent : fond de carte dans Carrière, icône et trait dans le schéma d'avant
 * connexion. `tint` est son fond clair, utilisé par le seul FeatureWheel. Les deux vont ensemble.
 */
export type FeatureKey = 'cv' | 'linkedin' | 'letter' | 'offers' | 'interview' | 'coach';

export interface FeatureAccent {
  color: string;
  tint: string;
  /** Variante ASSOMBRIE de `color`, lisible en TEXTE sur blanc. Voir `encrer`. */
  ink: string;
}

/** Contraste WCAG entre deux couleurs opaques. */
function contraste(a: string, b: string): number {
  const lum = (h: string) => {
    const c = [1, 3, 5]
      .map((i) => parseInt(h.substr(i, 2), 16) / 255)
      .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const [haut, bas] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (haut + 0.05) / (bas + 0.05);
}

/**
 * DÉRIVE une teinte lisible en TEXTE sur fond blanc, en assombrissant l'accent jusqu'au seuil AA.
 *
 * Pourquoi une dérivation et non une couleur choisie à côté : deux valeurs pour la même identité
 * finissent par diverger, et c'est exactement le défaut qu'on vient de corriger. Ici l'encre SUIT
 * l'accent — changer l'accent change l'encre, sans que personne ait à y penser.
 *
 * Le besoin est réel et mesuré : le corail de l'entretien affiche 3,09:1 sur blanc et le magenta de
 * la lettre 3,66:1, quand un texte courant demande 4,5:1. Utilisables tels quels pour une icône ou
 * un aplat — le seuil y est de 3:1 — mais pas pour un libellé.
 */
export const TEXT_CONTRAST_AA = 4.5;

export function encrer(couleur: string, cible: number = TEXT_CONTRAST_AA): string {
  let facteur = 1;
  let sortie = couleur;
  // Pas de 2 % : assez fin pour ne pas noircir inutilement, assez large pour converger vite.
  while (contraste(sortie, '#FFFFFF') < cible && facteur > 0.05) {
    facteur -= 0.02;
    const canaux = [1, 3, 5].map((i) =>
      Math.round(parseInt(couleur.substr(i, 2), 16) * facteur)
        .toString(16)
        .padStart(2, '0'),
    );
    sortie = `#${canaux.join('')}`.toUpperCase();
  }
  return sortie;
}

const feature = (color: string, tint: string): FeatureAccent => ({ color, tint, ink: encrer(color) });

export const FEATURE_ACCENTS: Record<FeatureKey, FeatureAccent> = {
  cv: feature(colors.blueVivid, colors.blueVividSoft),
  linkedin: feature(colors.violetVivid, colors.violetVividSoft),
  letter: feature(colors.magenta, colors.magentaSoft),
  offers: feature(colors.navyDeep, colors.navyDeepSoft),
  interview: feature(colors.coralVivid, colors.coralVividSoft),
  coach: feature(colors.indigo, colors.indigoSoft),
};
